import { useMutation } from '@tanstack/react-query'
import api from '../../config/axios-config'
import { planMutationKeys, planQueryKeys } from '../constants'
import { customQueryClient } from '../../config/query-client'

/**
 * @method post - move to a destination folder
 * @method delete - move to root folder (destinationFolderId is null)
 */

export const useMovePlansAndFoldersMutation = (options) => {
  const handleMutation = async ({
    planIds = [],
    folderIds = [],
    destinationFolderId = null,
    projectId
  }) => {
    const method = destinationFolderId ? 'post' : 'delete'

    // a folder can't be moved into itself: the API accepts it and the folder
    // then disappears from the tree. Move whatever else was selected and leave
    // the destination where it is
    const foldersToMove = folderIds.filter(
      (folderId) => folderId !== destinationFolderId
    )

    if (!foldersToMove.length && !planIds.length) {
      throw new Error('No items left to move after removing the destination')
    }

    const config = {
      method,
      data: {
        folders: foldersToMove,
        entities: planIds,
        projectId
      }
    }

    const { data } = await api(
      `/api/v2/plans/folders${destinationFolderId ? `/${destinationFolderId}` : ''}`,
      config
    )

    return data
  }

  const invalidateRelatedQueries = () => {
    customQueryClient.invalidateQueries({
      queryKey: [planQueryKeys.GET_PLANS_TREE_QUERY]
    })
    customQueryClient.invalidateQueries({
      queryKey: [planQueryKeys.GET_PLANS_TREE_INFINITY_QUERY]
    })

    customQueryClient.invalidateQueries({
      queryKey: [planQueryKeys.GET_PLANS_CHILDREN_TREE_QUERY]
    })

    // moved folders can carry nested plans whose ids aren't in planIds,
    // so invalidate every cached relative path instead of just planIds
    customQueryClient.invalidateQueries({
      queryKey: [planQueryKeys.GET_RELATIVE_PLAN_PATH]
    })
  }

  return useMutation({
    mutationKey: [planMutationKeys.MOVE_PLANS_AND_FOLDERS],
    mutationFn: handleMutation,
    retry: 0,
    ...options,

    onSuccess: (data, variables, context) => {
      invalidateRelatedQueries()

      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context)
    }
  })
}
